import React, { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';

interface SignUpFormProps {
  onToggleMode: () => void;
  redirect?: string;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onToggleMode, redirect }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { signUp } = useAuth();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    const { user, error } = await signUp(email, password, name, redirect);
    setLoading(false);
    if (error) {
      setError(error.message || 'Failed to sign up.');
    } else if (user) {
      setSuccess(true);
      setEmail('');
      setName('');
      setPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <>
      {/* Logo */}
      <div className="mb-10">
        <img src="/logo_horizontal.png" alt="NoteWell" className="h-8 w-auto" />
      </div>

      <h1 className="text-3xl font-semibold">Get started</h1>
      <p className="mt-2 text-sm text-zinc-500">Create a new account</p>

      {/* Error Banner */}
      {error && (
        <div className="mt-6 mb-8 p-4 bg-red-100 border border-red-200 rounded-lg">
          <div className="text-sm text-red-900">
            {error.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4 mt-8">
        {success && (
          <div className="text-green-600 text-sm text-center mb-2">
            Signup successful! Please check your email to verify your account before logging in.
          </div>
        )}
        <div>
          <label htmlFor="email" className="block text-sm mb-1">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label htmlFor="name" className="block text-sm mb-1">
            Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="Your full name"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 pr-10 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-zinc-500 hover:text-zinc-700 focus:outline-none"
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm mb-1">
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="••••••••"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-md bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing up…' : 'Sign Up'}
        </button>
      </form>

      {success && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onToggleMode}
            className="text-indigo-600 hover:underline text-sm font-medium"
          >
            Back to Login
          </button>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-zinc-500">
        Have an account?{' '}
        <button type="button" onClick={onToggleMode} className="text-zinc-900 hover:underline">
          Sign In Now
        </button>
      </p>

      <p className="mt-10 text-[11px] leading-5 text-zinc-400 text-center">
        By continuing, you agree to Notewell&apos;s{' '}
        <a
          href="https://www.notewell.ai/terms-of-service"
          className="underline hover:text-zinc-700"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://www.notewell.ai/privacy-policy" className="underline hover:text-zinc-700">
          Privacy Policy
        </a>
        , and to receive periodic emails with updates.
      </p>
    </>
  );
};

// ------------------------
// ICONS
// ------------------------
const EyeIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M1.5 12s4-7.5 10.5-7.5S22.5 12 22.5 12s-4 7.5-10.5 7.5S1.5 12 1.5 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const EyeOffIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 3l18 18M9.88 9.88A3.5 3.5 0 0114.12 14.12M6.1 6.33C3.9 7.92 2.25 10.1 1.5 12c0 0 4 7.5 10.5 7.5 2.02 0 3.83-.52 5.41-1.37M17.9 6.1C16.31 5.3 14.49 4.5 12 4.5 5.5 4.5 1.5 12 1.5 12c.43.8 1.05 1.77 1.89 2.76"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default SignUpForm;
